import { decryptWithDEK, encryptWithDEK } from "@/features/encryption/utils/encryption";

// Types for encrypted note data
export type EncryptedNoteContent =
  | {
      encrypted_data: string;
      iv: string;
    }
  | {
      encrypted_data: null;
      iv: null;
    };

export interface DecryptedNoteContent {
  text_content: string;
  html_content: string;
}

/**
 * Encrypt note content (text_content and html_content) before sending to server
 * Only encrypts if encryption is enabled
 */
export async function encryptNoteContent(
  textContent: string | null | undefined,
  htmlContent: string | null | undefined,
  dek: CryptoKey
): Promise<EncryptedNoteContent> {
  try {
    // Combine both contents into a single JSON object for encryption
    const contentToEncrypt = JSON.stringify({
      text_content: textContent || "",
      html_content: htmlContent || "",
    });
    const { iv, ciphertext } = await encryptWithDEK(dek, contentToEncrypt);
    return {
      encrypted_data: ciphertext,
      iv,
    };
  } catch (error) {
    console.error("Failed to encrypt note content:", error);
    return { encrypted_data: null, iv: null };
  }
}

/**
 * Decrypt note content received from server
 * Only decrypts if encryption is enabled and data is available
 */
export async function decryptNoteContent(
  encryptedData: string,
  iv: string,
  dek: CryptoKey
): Promise<DecryptedNoteContent> {
  try {
    const decrypted = await decryptWithDEK(dek, encryptedData, iv);
    const parsed = JSON.parse(decrypted);

    return {
      text_content: parsed.text_content || "",
      html_content: parsed.html_content || "",
    };
  } catch (error) {
    console.error("Failed to decrypt note content:", error);
    return {
      text_content: "",
      html_content: "",
    };
  }
}

/**
 * Process note content by decrypting if encryption is enabled
 * If public_uuid is provided, the note is shared and should not be encrypted
 * If the note was encrypted before, it will be decrypted
 * Returns decrypted content or original content if no encryption
 */
export async function processNoteContent(
  note: {
    text_content: string | null;
    html_content: string | null;
    encrypted_data: string | null;
    iv: string | null;
    shared?: boolean;
  },
  dek: CryptoKey | null | undefined
): Promise<{ text_content: string; html_content: string }> {
  // If note is shared (public_uuid provided), it should not be encrypted
  // Decrypt if it was previously encrypted
  if (note.shared) {
    if (note.encrypted_data && note.iv && dek) {
      return decryptNoteContent(note.encrypted_data, note.iv, dek);
    }
    return {
      text_content: note.text_content || "",
      html_content: note.html_content || "",
    };
  }

  // For non-shared notes, decrypt only if encryption is enabled
  if (dek && note.encrypted_data && note.iv) {
    return decryptNoteContent(note.encrypted_data, note.iv, dek);
  }
  return {
    text_content: note.text_content || "",
    html_content: note.html_content || "",
  };
}

/**
 * Prepare encrypted content for database operations
 * If public_uuid is provided, the note is shared and should not be encrypted
 * Returns encrypted data if available and note is not shared, otherwise null values
 */
export async function prepareEncryptedContent(
  textContent: string | null | undefined,
  htmlContent: string | null | undefined,
  dek: CryptoKey | null | undefined,
  shared?: boolean
): Promise<{
  encrypted_data: string | null;
  iv: string | null;
  clearContent: boolean;
}> {
  // If note is shared (public_uuid provided), do not encrypt
  if (shared) {
    return { encrypted_data: null, iv: null, clearContent: false };
  }

  if (!dek) {
    return { encrypted_data: null, iv: null, clearContent: false };
  }

  const result = await encryptNoteContent(textContent, htmlContent, dek);
  return {
    ...result,
    clearContent: result.encrypted_data !== null && result.iv !== null,
  };
}
